/**
 * RitualDetailPage
 * Main page for viewing ritual details. Supports all ritual formats:
 * - Self-guided ritual
 * - Self-guided course (enrolled/not enrolled)
 * - In-person ritual
 * - Live virtual ritual
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Archive,
  ArchiveRestore,
  Bookmark,
  EllipsisVertical,
  Flag,
  Pencil,
  Share,
} from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { ArrowLeftIcon } from "@/components/icons"
import { ReportModal } from "@/components/shared/ReportModal"
import { ShareModal } from "@/components/shared/ShareModal"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useToggleRitualSave } from "@/features/profile/hooks/useSavedRituals"
import { useIsMobile } from "@/hooks/useIsMobile"
import { useTranslation } from "@/language"
import { CourseModulesList } from "../components/CourseModulesList"
import { DiscussionSection } from "../components/detail/DiscussionSection"
import { ExploreMoreSection } from "../components/detail/ExploreMoreSection"
import {
  IntentionSection,
  StepByStepGuidanceSection,
  WhatYoullExperienceSection,
  WhatYoullNeedSection,
} from "../components/detail/RitualAccordionSections"
import { RitualCourseDays } from "../components/detail/RitualCourseDays"
import { RitualInfoSection } from "../components/detail/RitualInfoSection"
import { RitualMediaCarousel } from "../components/detail/RitualMediaCarousel"
import { RitualSidebar } from "../components/detail/RitualSidebar"
import { RitualTagsList } from "../components/detail/RitualTagsList"
import { useRitualOrganizers } from "../hooks/useRitualOrganizers"
import {
  archiveRitual,
  getRitualById,
  publishRitual,
  transformRitualDetailToUI,
  unarchiveRitual,
} from "../services/ritual.service"
import type { RitualDetailData, RitualOrganizer } from "../types/detail.types"

// ============================================================================
// Button State Helper
// ============================================================================

/**
 * Simple helper that computes button state from API response.
 * No complex logic - just derives UI state from backend data.
 *
 * Button behavior by format:
 * - In-Person/Virtual-Course: "JOIN" → "JOINED" + hamburger
 * - Self-guided: "JOIN" → "START RITUAL" + hamburger
 * - Self-guided-course: "JOIN" → "CONTINUE COURSE" + hamburger
 * - Paid rituals: "Buy - $X" → (format-dependent)
 */
function getRitualButtonState(
  ritual: RitualDetailData,
  currentUserId?: string | null,
  t?: (key: string, options?: Record<string, unknown>) => string,
) {
  const isCreator = ritual.createdBy === currentUserId
  const isOrganizer = ritual.isOrganizer
  const isMember = ritual.membershipDetails?.isMember
  const canAccess = ritual.membershipDetails?.canAccessContent
  const isJoined = isOrganizer || isMember
  const isPaid = ritual.isPaid

  // Self-guided ritual specific state
  const currentProgressStatus = ritual.currentProgressStatus
  const completedDays = ritual.completedDays ?? 0
  const canStartRitual = ritual.canStartRitual ?? true

  // Course progress specific state
  const courseProgress = ritual.courseProgress
  // Use backend-provided flags - showContinueCourse if actively doing course
  const showContinueCourse = courseProgress?.showContinueCourse ?? false

  // Determine button configuration based on format and membership
  let actionLabel: string
  let showHamburger = false
  let showStartButton = false
  let buttonVariant: "primary" | "secondary" = "secondary"

  if (!isJoined) {
    // Not joined - show JOIN, BUY, or REQUEST TO JOIN button
    const isInviteReq = ritual.whoCanJoin?.includes("invite_req") ?? false
    if (isPaid) {
      actionLabel = t
        ? t("ritualDetail.buttons.buy", { price: ritual.price ?? 0 })
        : `Buy - $${ritual.price ?? 0}`
    } else if (isInviteReq) {
      if (ritual.hasPendingRequest) {
        actionLabel = t ? t("ritualDetail.buttons.cancelRequest") : "CANCEL REQUEST"
      } else {
        actionLabel = t ? t("ritualDetail.buttons.requestToJoin") : "REQUEST TO JOIN"
      }
    } else {
      actionLabel = t ? t("ritualDetail.buttons.join") : "JOIN RITUAL"
    }
  } else {
    // Joined - buttons vary by format
    showHamburger = true

    switch (ritual.format) {
      case "in-person":
      case "virtual-course":
        actionLabel = t ? t("ritualDetail.buttons.joined") : "JOINED"
        break
      case "self-guided":
        if (currentProgressStatus === "IN_PROGRESS") {
          // User is currently doing the ritual
          actionLabel = t ? t("ritualDetail.buttons.markAsDone") : "MARK AS DONE"
          buttonVariant = "primary"
        } else {
          // User can start a new ritual
          const label = t ? t("ritualDetail.buttons.startRitual") : "START RITUAL"
          actionLabel = completedDays > 0 ? `${label} (${completedDays})` : label
        }
        showStartButton = true
        break
      case "self-guided-course":
        if (showContinueCourse) {
          // User is actively doing the course - show "CONTINUE COURSE"
          actionLabel = t ? t("ritualDetail.buttons.continueCourse") : "CONTINUE COURSE"
        } else {
          // User can start or restart - show "START COURSE"
          actionLabel = t ? t("ritualDetail.buttons.startCourse") : "START COURSE"
          buttonVariant = "secondary"
        }
        showStartButton = true
        break
      default:
        actionLabel = t ? t("ritualDetail.buttons.joined") : "JOINED"
    }
  }

  return {
    showActionButton: !isCreator,
    actionLabel,
    showHamburger: showHamburger && !isCreator,
    showStartButton,
    buttonVariant,
    canAccessContent: canAccess ?? false,
    canStartRitual,
    isInProgress: currentProgressStatus === "IN_PROGRESS",
    completedDays,
    courseProgress,
    isInviteReq: ritual.whoCanJoin?.includes("invite_req") ?? false,
    hasPendingRequest: ritual.hasPendingRequest ?? false,
  }
}

export type RitualButtonState = ReturnType<typeof getRitualButtonState>

export function RitualDetailScreen() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [showReportModal, setShowReportModal] = useState(false)
  const [showShareDialog, setShowShareDialog] = useState(false)
  const queryClient = useQueryClient()

  useEffect(() => {
    const payment = searchParams.get("payment")
    const donated = searchParams.get("donated")
    if (payment === "success") {
      toast.success("Payment successful! Your session has been booked.")
      setSearchParams({}, { replace: true })
    } else if (payment === "cancelled") {
      toast.error("Payment was cancelled.")
      setSearchParams({}, { replace: true })
    } else if (donated === "true") {
      toast.success("Thank you! Donation sent successfully.")
      queryClient.invalidateQueries({ queryKey: ["ritual", id, "members"] })
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams, id, queryClient])

  const isMobile = useIsMobile()
  // Translations
  const { t } = useTranslation("rituals")

  // Get current user
  const user = useAuthStore(state => state.user)

  // Toggle save hook
  const toggleRitualSave = useToggleRitualSave()

  // Publish ritual mutation
  const publishMutation = useMutation({
    mutationFn: (ritualId: string) => publishRitual(ritualId),
    onSuccess: () => {
      toast.success("Ritual published successfully")
      refetch()
      queryClient.invalidateQueries({ queryKey: ["user-ritual-teachings", user?.id] })
    },
  })

  // Archive ritual mutation
  const archiveMutation = useMutation({
    mutationFn: (ritualId: string) => archiveRitual(ritualId),
    onSuccess: () => {
      toast.success("Ritual archived")
      refetch()
      queryClient.invalidateQueries({ queryKey: ["user-ritual-teachings", user?.id] })
    },
  })

  // Unarchive ritual mutation
  const unarchiveMutation = useMutation({
    mutationFn: (ritualId: string) => unarchiveRitual(ritualId),
    onSuccess: () => {
      toast.success("Ritual unarchived")
      refetch()
      queryClient.invalidateQueries({ queryKey: ["user-ritual-teachings", user?.id] })
    },
  })

  // Handle publish ritual
  const handlePublishRitual = () => {
    if (id) {
      publishMutation.mutate(id)
    }
  }

  // Fetch ritual data from API
  const {
    data: apiRitual,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["ritual", id],
    queryFn: () => (id ? getRitualById(id) : Promise.reject(new Error("No id provided"))),
    enabled: !!id,
  })

  useEffect(() => {
    if (!isLoading && location.hash === "#discussion") {
      const el = document.getElementById("discussion")
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100)
      }
    }
  }, [isLoading, location.hash])

  // Transform API data to UI format
  const ritualData: RitualDetailData | undefined = apiRitual
    ? transformRitualDetailToUI(apiRitual)
    : undefined

  // Fetch ritual organizers with infinite scroll (includes isFollowing/isMember status)
  const { organizers: infiniteScrollOrganizers, pagination } = useRitualOrganizers({
    ritualId: id || "",
  })

  // Transform RitualOrganizerItem[] to RitualOrganizer[] for sidebar compatibility
  const transformedOrganizers: RitualOrganizer[] = useMemo(() => {
    return infiniteScrollOrganizers.map(org => ({
      id: org.id,
      organiserType: org.organiserType,
      organiserId: org.organiserId,
      user: org.user
        ? {
            ...org.user,
            isFollowing: org.user.isFollowing,
            followerCount: org.user.followerCount,
          }
        : null,
      community: org.community
        ? {
            id: org.community.id,
            name: org.community.name || "",
            handle: org.community.handle || "",
            coverImage: org.community.coverImage,
            isMember: org.community.isMember,
            totalMemberCount: org.community.totalMemberCount,
          }
        : null,
      name: org.user?.name || org.community?.name || "Unknown",
      avatar:
        org.user?.coverImage ||
        org.community?.coverImage ||
        org.user?.profilePictureUrl ||
        undefined,
      followers: 0,
    }))
  }, [infiniteScrollOrganizers])

  // Use isSaved directly from query data - React Query will keep it in sync
  const isSaved = ritualData?.isSaved ?? false

  // Handle bookmark toggle
  const handleBookmarkToggle = () => {
    toggleRitualSave.mutate(id || "")
  }

  // Handle report ritual
  const handleReportRitual = () => {
    setShowReportModal(true)
  }

  const hasEditAccess = ritualData?.canEdit ?? false

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-stone">{t("ritualDetail.loading")}</p>
      </div>
    )
  }

  // Error state
  if (isError || !ritualData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-red">{t("ritualDetail.error")}</p>
      </div>
    )
  }

  const hasSteps = ritualData.stepByStepGuidance && ritualData.stepByStepGuidance.length > 0
  const hasCourseDays = ritualData.courseDays && ritualData.courseDays.length > 0

  // Compute button state from API response
  const buttonState = getRitualButtonState(ritualData, user?.id, t)
  const discussionPermissions = ritualData.discussionPermissions || {
    canComment: false,
    canReply: false,
  }

  return (
    <div className="w-full md:mt-5 max-w-360 mx-auto">
      {/* Media Carousel with Back Button and Action Buttons Overlay */}
      <div className="relative">
        {/* Back Button */}
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate(-1)}
          className="hidden md:flex absolute top-5.5 p-5 left-17.5 z-20 rounded-md border-gradient-glass"
        >
          <ArrowLeftIcon size="5" color="white" />
        </Button>

        {/* Action Buttons - Top Right */}
        <div className="hidden md:flex items-center gap-2.5 absolute top-5 right-17 z-20">
          {hasEditAccess && (
            <Button
              variant="ghost"
              size="sm"
              className="rounded-md border-gradient-glass  h-11 w-11"
              onClick={() => navigate(`/rituals/edit/${id}`)}
              aria-label={t("ritualHeader.editRitual")}
            >
              <Pencil className="!w-5 !h-5 text-white" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="border-gradient-glass rounded-md h-11 w-11"
            onClick={handleBookmarkToggle}
            disabled={toggleRitualSave.isPending}
            aria-label={isSaved ? t("ritualHeader.removeBookmark") : t("ritualHeader.addBookmark")}
          >
            <Bookmark
              className={`!w-5 !h-5 ${isSaved ? "fill-current text-white" : "text-white"}`}
            />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowShareDialog(true)}
            aria-label={t("ritualHeader.shareRitual")}
            className="rounded-md border-gradient-glass h-11 w-11"
          >
            <Share className="!w-5 !h-5 text-white " />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                aria-label={t("ritualHeader.moreOptions")}
                className="rounded-md border-gradient-glass h-11 w-11"
              >
                <EllipsisVertical className="!w-5 !h-5 text-white" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-lg p-3">
              {!ritualData.isLive && (
                <DropdownMenuItem
                  onClick={handlePublishRitual}
                  disabled={publishMutation.isPending}
                  className="cursor-pointer"
                >
                  {publishMutation.isPending ? "Publishing..." : "Publish"}
                </DropdownMenuItem>
              )}
              {ritualData.isOwner && !ritualData.isArchived && (
                <DropdownMenuItem
                  onClick={() => {
                    if (id) archiveMutation.mutate(id)
                  }}
                  disabled={archiveMutation.isPending}
                  className="cursor-pointer"
                >
                  <Archive className="mr-2 size-4" />
                  {archiveMutation.isPending ? "Archiving..." : "Archive"}
                </DropdownMenuItem>
              )}
              {ritualData.isOwner && ritualData.isArchived && (
                <DropdownMenuItem
                  onClick={() => {
                    if (id) unarchiveMutation.mutate(id)
                  }}
                  disabled={unarchiveMutation.isPending}
                  className="cursor-pointer"
                >
                  <ArchiveRestore className="mr-2 size-4" />
                  {unarchiveMutation.isPending ? "Unarchiving..." : "Unarchive"}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={handleReportRitual}
                className="text-brand-terracotta cursor-pointer"
              >
                <Flag className="mr-2 size-4" />
                {t("ritualHeader.report")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Media Carousel */}
        <RitualMediaCarousel media={ritualData.media} className="[1400px]:rounded-[30px]" />
      </div>

      {/* Main Content - 2 Column Layout */}
      <div className="py-7.5 md:py-10 px-5 md:px-17.5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-7.5">
          {/* Left Column */}
          <div className="lg:col-span-8 space-y-7.5">
            {/* Title and Description */}
            <RitualInfoSection
              title={ritualData.title}
              description={ritualData.description}
              ritualData={ritualData}
              buttonState={buttonState}
              onActionSuccess={() => refetch()}
            />
            <div className="space-y-5">
              {/* Accordion Sections - Hide for PERSONAL visibility */}
              {ritualData.visibility !== "PERSONAL" && (
                <>
                  <WhatYoullNeedSection
                    items={ritualData.whatYoullNeed ?? []}
                    defaultOpen={!isMobile}
                  />

                  <WhatYoullExperienceSection
                    items={ritualData.whatYoullExperience ?? []}
                    defaultOpen={!isMobile}
                  />
                </>
              )}

              {/* Step-by-Step Guidance (self-guided rituals) */}
              {hasSteps && ritualData.stepByStepGuidance && (
                <StepByStepGuidanceSection
                  steps={ritualData.stepByStepGuidance}
                  canAccess={buttonState.canAccessContent}
                  defaultOpen={!isMobile}
                />
              )}

              {/* Course Days (self-guided courses) */}
              {hasCourseDays && ritualData.courseDays && (
                <RitualCourseDays days={ritualData.courseDays} defaultOpen={!isMobile} />
              )}

              {/* Course Modules (self-guided courses with video modules).
                  Also surfaces any audio media uploaded with the ritual as
                  appended items in the same list, with a music-note icon. */}
              {(() => {
                const audioMediaItems = ritualData.media?.filter(m => m.type === "audio") ?? []
                const audioCoverImageUrl =
                  ritualData.media?.find(m => m.type === "image" && m.isCover)?.url ||
                  ritualData.media?.find(m => m.type === "image")?.url
                const hasModules = ritualData.courseModules && ritualData.courseModules.length > 0
                if (!hasModules && audioMediaItems.length === 0) return null
                return (
                  <CourseModulesList
                    modules={ritualData.courseModules ?? []}
                    audioItems={audioMediaItems}
                    audioCoverImageUrl={audioCoverImageUrl}
                    ritualId={id}
                    canAccessContent={buttonState.canAccessContent}
                  />
                )
              })()}

              {/* Intention */}
              {ritualData.intention && (
                <IntentionSection intention={ritualData.intention} defaultOpen={!isMobile} />
              )}
            </div>

            {/* Tags */}
            <RitualTagsList tags={ritualData.tags} />

            {!isMobile && (
              <DiscussionSection
                entityId={ritualData.id}
                canComment={discussionPermissions.canComment}
                canReply={discussionPermissions.canReply}
                canModerate={Boolean(ritualData.isOrganizer || ritualData.createdBy === user?.id)}
              />
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="lg:col-span-4">
            <RitualSidebar
              data={ritualData}
              buttonState={buttonState}
              onActionSuccess={() => refetch()}
              organizers={transformedOrganizers}
              totalOrganizers={pagination?.totalItems}
            />
          </div>
        </div>
      </div>
      {isMobile && (
        <div className="px-5 ">
          <DiscussionSection
            entityId={ritualData.id}
            canComment={discussionPermissions.canComment}
            canReply={discussionPermissions.canReply}
            canModerate={Boolean(ritualData.isOrganizer || ritualData.createdBy === user?.id)}
          />
        </div>
      )}

      {/* Explore More */}
      <ExploreMoreSection currentRitualId={id || ""} />

      {/* Report Modal */}
      {ritualData && (
        <ReportModal
          open={showReportModal}
          onOpenChange={setShowReportModal}
          target="RITUAL"
          targetData={{
            id: ritualData.id,
            name: ritualData.title,
            handle: ritualData.organizers?.[0]?.user?.username || undefined,
            imageUrl:
              ritualData.media?.find(m => m.isCover)?.url || ritualData.media?.[0]?.url || null,
          }}
        />
      )}

      {/* Share to Social Dialog */}
      {ritualData && (
        <ShareModal
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
          shareUrl={`/rituals/${ritualData.id}`}
          shareText={ritualData.title}
          displayPath={`/rituals/${ritualData.title.toLowerCase().replace(/\s+/g, "-")}`}
        />
      )}
    </div>
  )
}
